/**
 * StandardArticlesPage — eigenständige Seite (peer-level zu Kunden + Projekte).
 *
 * Erreichbar via /standard-articles, gerendert über die SubNav „Kunden / Projekte / Standardartikel".
 *
 * Stil/Pattern-Parallel zu Customers.js und Projects.js:
 *   - h1 + Subtitle als Page-Header
 *   - „+ Neuer Artikel"-Button rechts oben
 *   - Tabelle aller aktiven Standard-Artikel
 *   - Soft-Delete (is_active=false)
 */
import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Package } from 'lucide-react';
import { CurrencyInput } from '@/components/inputs/SmartInputs';
import { useUserCountry } from '@/hooks/useUserCountry';
import { getVatRatesForCountry } from '@/utils/vatRates';
import { formatCurrency } from '@/utils/i18nValidators';
import ConfirmDialog from '@/components/ConfirmDialog';

const DEFAULT_FORM = {
  name: '',
  description: '',
  default_quantity: 1,
  default_unit: 'Std',
  unit_price: 0,
  vat_rate: 19,
};

const UNIT_OPTIONS = ['Std', 'Stk', 'Tag', 'Monat', 'Jahr', 'Pauschal', 'kg', 'm', 'm²'];

export default function StandardArticlesPage() {
  const { user } = useAuth();
  const { country } = useUserCountry();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null });
  const [form, setForm] = useState(DEFAULT_FORM);

  useEffect(() => {
    if (user?.country === 'AT') setForm((prev) => ({ ...prev, vat_rate: 20 }));
    else if (user?.country === 'CH') setForm((prev) => ({ ...prev, vat_rate: 8.1 }));
    fetchArticles();
  }, []); // eslint-disable-line

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const res = await api.get('/standard-articles');
      setArticles(res.data.articles || []);
    } catch (e) {
      console.error('Error fetching standard articles:', e);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({
      ...DEFAULT_FORM,
      vat_rate: user?.country === 'AT' ? 20 : user?.country === 'CH' ? 8.1 : 19,
    });
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || '',
      description: a.description || '',
      default_quantity: a.default_quantity ?? 1,
      default_unit: a.default_unit || 'Std',
      unit_price: a.unit_price ?? 0,
      vat_rate: a.vat_rate ?? 19,
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Bitte Name eingeben.');
      return;
    }
    try {
      if (editing) {
        await api.put(`/standard-articles/${editing.id}`, form);
        toast.success('Artikel aktualisiert');
      } else {
        await api.post('/standard-articles', form);
        toast.success('Artikel angelegt');
      }
      setDialogOpen(false);
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const requestDelete = (id) => setConfirmDialog({ isOpen: true, id });

  const confirmDelete = async () => {
    if (!confirmDialog.id) return;
    try {
      await api.delete(`/standard-articles/${confirmDialog.id}`);
      toast.success('Artikel gelöscht');
      setConfirmDialog({ isOpen: false, id: null });
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  return (
    <div className="space-y-8" data-testid="standard-articles-page">
      <div className="flex justify-between items-center">
        <div>
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Standardartikel
          </h1>
          <p className="text-lg text-gray-600">
            Wiederkehrende Rechnungspositionen einmal anlegen, in jeder Rechnung mit einem Klick einfügen.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-standard-article-btn">
              <Plus className="h-4 w-4 mr-2" />
              Neuer Artikel
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Standardartikel bearbeiten' : 'Neuer Standardartikel'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="z.B. Stundensatz Senior, Hosting Premium"
                  required
                  data-testid="article-name-input"
                />
              </div>
              <div>
                <Label>Beschreibung (optional)</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Wird beim Einfügen in eine Rechnung als Beschreibung übernommen."
                  rows={2}
                  data-testid="article-description-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Menge</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.default_quantity}
                    onChange={(e) => setForm({ ...form, default_quantity: parseFloat(e.target.value) || 0 })}
                    data-testid="article-quantity-input"
                  />
                </div>
                <div>
                  <Label>Einheit</Label>
                  <select
                    value={form.default_unit}
                    onChange={(e) => setForm({ ...form, default_unit: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="article-unit-select"
                  >
                    {UNIT_OPTIONS.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>USt-Satz (%)</Label>
                  <select
                    value={form.vat_rate}
                    onChange={(e) => setForm({ ...form, vat_rate: parseFloat(e.target.value) })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="article-vat-select"
                  >
                    {getVatRatesForCountry(country).map((r) => (
                      <option key={r.rate} value={r.rate}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label>Einzelpreis netto</Label>
                <CurrencyInput
                  country={country}
                  value={form.unit_price}
                  onChange={(num) => setForm({ ...form, unit_price: num ?? 0 })}
                  label={null}
                  data-testid="article-unit-price-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" data-testid="article-save-btn">
                  {editing ? 'Speichern' : 'Anlegen'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Lade Artikel…</div>
      ) : articles.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Noch keine Standardartikel angelegt.</p>
            <p className="text-xs text-slate-400 mt-1">
              Lege wiederkehrende Rechnungspositionen einmal an und nutze sie überall.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold">Name</th>
                  <th className="text-left py-3 px-4 font-semibold">Beschreibung</th>
                  <th className="text-right py-3 px-4 font-semibold">Menge × Einheit</th>
                  <th className="text-right py-3 px-4 font-semibold">Preis</th>
                  <th className="text-right py-3 px-4 font-semibold">USt</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`article-row-${a.id}`}
                  >
                    <td className="py-3 px-4 font-medium text-slate-900">{a.name}</td>
                    <td className="py-3 px-4 text-slate-600 text-xs max-w-xs truncate">{a.description || '—'}</td>
                    <td className="py-3 px-4 text-right text-slate-700">
                      {a.default_quantity} <span className="text-slate-400">{a.default_unit}</span>
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-slate-900">
                      {formatCurrency(a.unit_price, country)}
                    </td>
                    <td className="py-3 px-4 text-right text-slate-600">{a.vat_rate}%</td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`article-edit-${a.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => requestDelete(a.id)}
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`article-delete-${a.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, id: null })}
        onConfirm={confirmDelete}
        title="Standardartikel löschen?"
        description="Bestehende Rechnungen mit diesem Artikel bleiben unverändert. Du kannst ihn nur nicht mehr in neue Rechnungen einfügen."
        confirmText="Löschen"
        variant="destructive"
      />
    </div>
  );
}
